import { createRef, Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter, generatePath } from 'react-router-dom';
import styled from 'styled-components';
import getComponentName from 'react-utils/getComponentName';
import { NavMarker } from 'react-rhumb';
import * as ReduxRequestStates from 'redux-request-state/requestStates';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import { getFlowRevisionRoute } from 'automation-ui-routing/utils';
import { getFlowRevision, getFlowRevisionRequestStatus } from 'automation-ui-canvas/selectors/flowSelectors';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import { isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import { trackPageView } from 'automation-ui-canvas/utils/trackingUtils';
import * as flowActions from 'automation-ui-canvas/actions/FlowActions';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { basePaths } from 'automation-ui-canvas/constants/Routes';
import LoadingError from 'automation-ui-canvas/components/errors/LoadingError';
import ActionConfigBasePaths from 'automation-ui-action-features/actions/ActionConfigBasePaths';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { EVENT_ENROLLMENT_CONFIG_REVISION_PREVIEW, SCHEDULED_ENROLLMENT_CONFIG_REVISION_PREVIEW, LIST_ENROLLMENT_CONFIG_REVISION_PREVIEW, TRIGGER_SELECTION_REVISION_PREVIEW } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getEnrollmentConfigRoute } from 'automation-ui-canvas/utils/absoluteRoutingUtils';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasRevisionPreviewDecorator__StyledCanvas",
  componentId: "sc-7zeu9f-0"
})(["height:100%;"]);
const StyledUIOptimisticNanoProgress = styled(UIOptimisticNanoProgress).withConfig({
  displayName: "FlowCanvasRevisionPreviewDecorator__StyledUIOptimisticNanoProgress",
  componentId: "sc-7zeu9f-1"
})(["position:absolute;top:0;left:0;right:0;"]);
function getComposedCanvas() {
  return PannableDecorator(
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
export default function FlowCanvasRevisionPreviewDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasRevisionPreview extends Component {
    constructor(...args) {
      super(...args);
      this.attachScrollEventListener = canvasScrollElementRef => {
        if (canvasScrollElementRef && canvasScrollElementRef.current) {
          canvasScrollElementRef.current.addEventListener('scroll', this.handleScroll);
        }
      };
      this.removeScrollEventListener = canvasScrollElementRef => {
        if (canvasScrollElementRef && canvasScrollElementRef.current) {
          canvasScrollElementRef.current.removeEventListener('scroll', this.handleScroll);
        }
      };
      this.handleScroll = event => {
        const scrollLeft = event.target.scrollLeft;
        if (this.headerRef.current) {
          this.headerRef.current.style.transform = `translateX(${scrollLeft}px)`;
        }
      };
      this.getComposedProps = () => {
        const {
          flowRevision,
          canvasScrollElementRef,
          zoomLevel
        } = this.props;
        return {
          // might be a miscast but downstream components assume flow is defined
          flow: flowRevision ? flowRevision.flow : null,
          canvasScrollElementRef,
          onSelectAction: this.onSelectAction,
          ComposedCanvas: this.ComposedCanvas,
          readOnlyOverride: true,
          // Used for permission overrides like revisions
          centerOnMount,
          zoomLevel
        };
      };
      this.handleRequestFlowRevision = (props = this.props) => {
        const {
          requestFlowRevision,
          flowId,
          revisionId
        } = props;
        if (flowId && !isNaN(revisionId)) {
          requestFlowRevision({
            flowId,
            revisionId
          });
        }
      };
      this.onSelectAction = id => {
        const {
          flow,
          history,
          flowId,
          revisionId,
          match: {
            params: {
              panel
            }
          },
          location: {
            state
          }
        } = this.props;
        const isChangesRollupPanelOpen = panel === basePaths.CHANGES_ROLLUP || state && state.restoreChangesPanel;
        const previewPath = `${getFlowRevisionRoute(flowId, revisionId)}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_CONFIGS}`;
        const action = flow.getActionById(id);
        const actionType = action ? action.getActionType() : null;
        const configRoute = actionType ? ActionConfigBasePaths[actionType] : null;
        let pathname = `${previewPath}/${id}/${configRoute}`;
        if (id === SpecialActionIds.ENROLLMENT) {
          pathname = getEnrollmentConfigRoute({
            flow: flow,
            revisionId
          });
        } else if (isGoToActionId(id)) {
          // There is nothing you can do with go-to actions in readonly mode; ignore clicks on them
          return;
        } else if (id === CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE) {
          const virtualConfigRoute = ActionConfigBasePaths[ActionTypes.CLASSIC_VIRTUAL_ANCHORED_DELAY];
          pathname = `${previewPath}/${id}/${virtualConfigRoute}`;
        }
        history.replace({
          pathname,
          state: {
            doNotScrollTo: true,
            restoreChangesPanel: isChangesRollupPanelOpen
          }
        });
      };
      this.ComposedCanvas = getComposedCanvas();
      this.headerRef = /*#__PURE__*/createRef();
    }
    componentDidMount() {
      const {
        canvasScrollElementRef
      } = this.props;
      this.handleRequestFlowRevision(this.props);
      trackPageView(UsageTrackingScreens.PLATFORM_FLOW_REVISION_PREVIEW_CONTAINER);
      this.attachScrollEventListener(canvasScrollElementRef);
    }
    componentDidUpdate(prevProps) {
      const {
        canvasScrollElementRef
      } = this.props;
      this.attachScrollEventListener(canvasScrollElementRef);
      if (prevProps.flowId !== this.props.flowId || prevProps.revisionId !== this.props.revisionId) {
        this.handleRequestFlowRevision(this.props);
      }
    }
    componentWillUnmount() {
      const {
        canvasScrollElementRef
      } = this.props;
      this.removeScrollEventListener(canvasScrollElementRef);
    }
    getTriggerSelectionRoute() {
      return generatePath(TRIGGER_SELECTION_REVISION_PREVIEW, {
        flowId: this.props.flowId,
        revisionId: this.props.revisionId
      });
    }
    getEventEnrollmentRoute() {
      return generatePath(EVENT_ENROLLMENT_CONFIG_REVISION_PREVIEW, {
        flowId: this.props.flowId,
        revisionId: this.props.revisionId
      });
    }
    getListEnrollmentRoute() {
      return generatePath(LIST_ENROLLMENT_CONFIG_REVISION_PREVIEW, {
        flowId: this.props.flowId,
        revisionId: this.props.revisionId
      });
    }
    getScheduledEnrollmentRoute() {
      return generatePath(SCHEDULED_ENROLLMENT_CONFIG_REVISION_PREVIEW, {
        flowId: this.props.flowId,
        revisionId: this.props.revisionId
      });
    }
    render() {
      const {
        flowRevisionRequestStatus
      } = this.props;
      if (flowRevisionRequestStatus === ReduxRequestStates.FAILED) {
        return /*#__PURE__*/_jsx(LoadingError, {
          error: 404
        });
      }
      const isLoading = flowRevisionRequestStatus === ReduxRequestStates.UNINITIALIZED || flowRevisionRequestStatus === ReduxRequestStates.STARTED;
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "REVISION_PREVIEW_CANVAS",
        children: /*#__PURE__*/_jsxs(ScreenTrackingContextProvider, {
          value: UsageTrackingScreens.PLATFORM_FLOW_REVISION_PREVIEW_CONTAINER,
          children: [isLoading && /*#__PURE__*/_jsx(StyledUIOptimisticNanoProgress, {
            value: 10
          }), /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps()))]
        })
      });
    }
  }
  const ConnectedFlowCanvasRevisionPreview = compose(wireAuthConsumer, withRouter, connect((state, ownProps) => {
    const {
      flowId: stringId,
      revisionId: stringRevisionId
    } = ownProps.match.params;
    const flowId = Number(stringId);
    const revisionId = Number(stringRevisionId);
    const flowRevision = getFlowRevision(flowId, revisionId)(state);
    const flowRevisionRequestStatus = getFlowRevisionRequestStatus(state);
    const flow = flowRevision ? flowRevision.flow : null;
    return {
      flowRevision,
      flowRevisionRequestStatus,
      flow,
      flowId,
      revisionId,
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, {
    requestFlowRevision: flowActions.requestFlowRevision
  }))(FlowCanvasRevisionPreview);
  ConnectedFlowCanvasRevisionPreview.displayName = `ConnectedFlowCanvasRevisionPreview(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasRevisionPreview, WrappedComponent);
  return ConnectedFlowCanvasRevisionPreview;
}